import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import BasicCustomTable from "../../../component/basicTable/index";
import TextFieldCustom from "../../../component/elements/textfieldCustom";
import axiosInstance from "../../../configuration/request";
import { useSnackbar } from "../../../component/SnackbarContext";
import useDebounce from "../../../utils/useDebounce";
import { adminOrderTrackingColumns } from "../../../constants/adminOrderTrackingTable";
import { orderStatusOptions } from "../../../constants/bussiness";
import { getUserManagementPermissions } from "../../../utils/rolesAndPermissions";
import { getOrderHolder } from "../../../constants/orderLifecycle";
import {
  getPartnerFromPickups,
  resolveDeliverySchedule,
  resolvePickupSchedule,
} from "../../../utils/orderTrackingHelpers";

function mapOrderRow(order) {
  const pickupPartner = getPartnerFromPickups(order?.pickups, "pickup");
  const deliveryPartner = getPartnerFromPickups(order?.pickups, "delivery");

  return {
    id: order?.id,
    order_id: order?.order_id || "—",
    customer_name: order?.user?.name || "—",
    pickup_datetime: resolvePickupSchedule(order),
    delivery_datetime: resolveDeliverySchedule(order),
    store_name: order?.store?.storeName || "—",
    pickup_partner: pickupPartner.display,
    delivery_partner: deliveryPartner.display,
    status_tag: order?.status || "—",
    order_holder: getOrderHolder(order?.status),
    rawOrder: order,
  };
}

function OrderTracking() {
  const navigate = useNavigate();
  const showSnackbar = useSnackbar();

  const [orders, setOrders] = useState([]);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");

  const debouncedSearch = useDebounce(searchTerm, 500);

  const permissions = useSelector((state) => state.counter.permissions);
  const rolePermissions = permissions?.[0]?.permissions || [];
  const { canView } = getUserManagementPermissions(rolePermissions);

  const columns = useMemo(() => {
    if (!canView) {
      return adminOrderTrackingColumns.filter((col) => col.field !== "action");
    }
    return adminOrderTrackingColumns;
  }, [canView]);

  const fetchOrders = useCallback(
    (pageNumber = 1, limit = 10, search = "") => {
      const params = {
        page: pageNumber,
        limit,
        order_status: orderStatusOptions.join(","),
      };

      if (search.trim()) {
        params.search = search.trim();
      }

      axiosInstance
        .get("/customer/orders", { params })
        .then((res) => {
          const formatted = (res?.data?.data || []).map(mapOrderRow);
          setOrders(formatted);
          setPage(
            Number(res?.data?.meta?.page || res?.data?.page || pageNumber)
          );
          setTotalCount(
            Number(res?.data?.meta?.total || res?.data?.total || 0)
          );
        })
        .catch((error) => {
          showSnackbar(
            error?.response?.data?.message || "Failed to fetch orders",
            "error"
          );
        });
    },
    [showSnackbar]
  );

  useEffect(() => {
    fetchOrders(page, rowsPerPage, debouncedSearch);
  }, [debouncedSearch, page, rowsPerPage, fetchOrders]);

  const handlePageChangeRequest = (newPage, newRowsPerPage) => {
    setPage(newPage);
    setRowsPerPage(newRowsPerPage);
  };

  const handleSearchFilter = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const handleView = (row) => {
    navigate(`/app/order-tracking/view/${row.id}`);
  };

  return (
    <div className="px-4 md:px-6">
      <div className="flex flex-col gap-4 py-4">
        <div>
          <h2 className="text-2xl font-semibold text-slate-900">
            Order Tracking
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Monitor all orders, partner assignments, and lifecycle status.
          </p>
        </div>

        <div className="flex w-full max-w-[380px]">
          <TextFieldCustom
            showSearchIcon
            placeholder="Search by order ID or customer name"
            customClass="color-search-textfield"
            fullWidth
            value={searchTerm}
            onChange={handleSearchFilter}
          />
        </div>

        <BasicCustomTable
          columns={columns}
          rows={orders}
          onView={handleView}
          colorHeader={true}
          customClassHeader="table-header-cell-colored"
          tableName="order tracking"
          maxHeightClass="max-h-[calc(100vh-300px)]"
          backendPagination={true}
          totalCount={totalCount}
          onPageChangeRequest={handlePageChangeRequest}
          pageLimit={rowsPerPage}
          page={page}
          canView={canView}
          emptyState="No orders found"
        />
      </div>
    </div>
  );
}

export default OrderTracking;
